import {  useEffect } from 'react';
import { message } from 'antd';
import type { UsePayPalButtonsParams } from '../../interfaces';
import { capturePayPalOrder, createPayPalOrder } from '../../../services';
import { getPayPalWindow, suppressExpectedPayPalScriptErrors } from '../../../utils/paypal';
import { getApiErrorMessage } from '../../../utils/apiError';
import { getShippingAddress } from './getShippingAddress';

export const usePayPalButtons = ({
  dispatch,
  navigate,
  paypalClientId,
  paypalContainerRef,
  paypalSdkStatus,
  setPayPalError,
  setPayPalProcessing,
  shipping,
  userId,
  guestEmail,
  guestCartItems,
}: UsePayPalButtonsParams) => {
  useEffect(() => {
    const paypalWindow = getPayPalWindow();
    const paypalContainer = paypalContainerRef.current;
    if (
      paypalSdkStatus !== 'ready' ||
      !paypalClientId ||
      !paypalContainer ||
      !paypalWindow.paypal
    ) {
      return;
    }

    let active = true;
    paypalContainer.innerHTML = '';

    const buttons = paypalWindow.paypal.Buttons({
      style: {
        layout: 'vertical',
        shape: 'rect',
        height: 46,
        label: 'paypal',
      },
      createOrder: async () => {
        setPayPalError(null);
        const shippingAddress = getShippingAddress(shipping);
        try {
          const payload = await dispatch(createPayPalOrder({
            shippingAddress,
            guestEmail,
            guestCartItems,
          })).unwrap();
          return payload.orderId;
        } catch (error) {
          const errorMessage = getApiErrorMessage(error, 'Unable to start PayPal checkout.');
          setPayPalError(errorMessage);
          message.error(errorMessage);
          throw new Error(errorMessage);
        }
      },
      onApprove: async (data: { orderID?: string }) => {
        if (!data.orderID) {
          throw new Error('PayPal did not return order id');
        }

        setPayPalProcessing(true);
        setPayPalError(null);

        try {
          await dispatch(capturePayPalOrder(data.orderID)).unwrap();
          navigate('/checkout/success', {
            replace: true,
            state: {
              paymentConfirmed: true,
              paymentReferenceId: data.orderID,
              paymentMethod: 'PayPal',
              isAuthenticatedOrder: Boolean(userId),
            },
          });
        } catch (error) {
          const errorMessage = getApiErrorMessage(error, 'PayPal payment failed.');
          setPayPalError(errorMessage);
          message.error(errorMessage);
        } finally {
          if (active) {
            setPayPalProcessing(false);
          }
        }
      },
      onError: (error: unknown) => {
        const errorMessage = getApiErrorMessage(error, 'Failed to initialize PayPal.');
        setPayPalError(errorMessage);
      },
      onCancel: () => {
        suppressExpectedPayPalScriptErrors();
        message.info('PayPal checkout was cancelled.');
        navigate('/checkout/cancel', { state: { paymentMethod: 'PayPal' } });
      },
    });

    if (buttons.isEligible && !buttons.isEligible()) {
      setPayPalError('PayPal is not available for this device or account.');
      return;
    }

    buttons.render(paypalContainer).catch(() => {
      setPayPalError('Failed to render PayPal button.');
    });

    return () => {
      active = false;
      paypalContainer.replaceChildren();
      Promise.resolve(buttons.close?.()).catch(() => undefined);
    };
  }, [
    dispatch,
    navigate,
    paypalClientId,
    paypalContainerRef,
    paypalSdkStatus,
    setPayPalError,
    setPayPalProcessing,
    shipping,
    userId,
    guestEmail,
    guestCartItems,
  ]);
};
